import { AlertCircle, Wrench, Settings2, User, Bot, Paperclip, Mic, Square, Send, X, Plus, Loader2, CheckCircle2, Code2, Globe, Search, TerminalSquare, Edit3, FileText, Hammer } from 'lucide-react';
import clsx from 'clsx';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useMemo, useId, useEffect } from 'react';

function cn(...inputs) {
  return clsx(...inputs);
}
var ROLE_ICON = {
  assistant: Bot,
  user: User,
  system: Settings2,
  "tool-call": Wrench,
  "tool-result": Wrench,
  error: AlertCircle
};
function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString(void 0, {
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
}
function MessageBubble({
  role,
  author,
  timestamp,
  toolName,
  className,
  children,
  ...rest
}) {
  const Icon = ROLE_ICON[role];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "article",
      className: cn("ds-msg", `ds-msg--${role}`, className),
      "data-role": role,
      ...rest,
      children: [
        /* @__PURE__ */ jsx("div", { className: "ds-msg__avatar", "aria-hidden": "true", children: Icon ? /* @__PURE__ */ jsx(Icon, { size: 16 }) : null }),
        /* @__PURE__ */ jsxs("div", { className: "ds-msg__body", children: [
          /* @__PURE__ */ jsxs("div", { className: "ds-msg__head", children: [
            /* @__PURE__ */ jsxs("span", { className: "ds-msg__author", children: [
              author ?? defaultAuthor(role),
              toolName && /* @__PURE__ */ jsx("code", { className: "ds-msg__tool", "aria-label": `\u0418\u043D\u0441\u0442\u0440\u0443\u043C\u0435\u043D\u0442: ${toolName}`, children: toolName })
            ] }),
            timestamp && /* @__PURE__ */ jsx("time", { className: "ds-msg__time", children: formatTime(timestamp) })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "ds-msg__content", children })
        ] })
      ]
    }
  );
}
function defaultAuthor(role) {
  switch (role) {
    case "assistant":
      return "\u0410\u0433\u0435\u043D\u0442";
    case "user":
      return "\u0412\u044B";
    case "system":
      return "\u0421\u0438\u0441\u0442\u0435\u043C\u0430";
    case "tool-call":
    case "tool-result":
      return "\u0418\u043D\u0441\u0442\u0440\u0443\u043C\u0435\u043D\u0442";
    case "error":
      return "\u041E\u0448\u0438\u0431\u043A\u0430";
  }
}
var IconButton = forwardRef(function IconButton2({ icon, size = "md", variant = "ghost", className, type = "button", ...rest }, ref) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type,
      className: cn(
        "ds-icon-button",
        `ds-icon-button--${size}`,
        `ds-icon-button--${variant}`,
        className
      ),
      ...rest,
      children: /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "ds-icon-button__icon", children: icon })
    }
  );
});
var MODE_LABELS = {
  plan: "\u041F\u043B\u0430\u043D",
  auto: "\u0410\u0432\u0442\u043E",
  confirm: "\u041F\u043E\u0434\u0442\u0432.",
  research: "\u0418\u0441\u0441\u043B\u0435\u0434.",
  chat: "\u0427\u0430\u0442"
};
var SURFACE_LABELS = {
  chat: "Chat",
  cowork: "Cowork",
  code: "Code"
};
function Composer({
  value,
  defaultValue,
  onChange,
  onSubmit,
  onAttach,
  onVoice,
  placeholder = "\u041E\u043F\u0438\u0448\u0438\u0442\u0435 \u0437\u0430\u0434\u0430\u0447\u0443 \u0438\u043B\u0438 \u0437\u0430\u0434\u0430\u0439\u0442\u0435 \u0432\u043E\u043F\u0440\u043E\u0441\u2026",
  disabled = false,
  busy = false,
  onStop,
  mode = "auto",
  onModeChange,
  modes = ["plan", "auto", "confirm", "research", "chat"],
  surfaceMode = "chat",
  onSurfaceModeChange,
  slashSkills = []
}) {
  const [internal, setInternal] = useState(defaultValue ?? "");
  const taRef = useRef(null);
  const isControlled = value !== void 0;
  const text = isControlled ? value : internal;
  const setText = (v) => {
    const ta = taRef.current;
    let restore = null;
    if (ta && document.activeElement === ta) {
      restore = {
        start: ta.selectionStart ?? v.length,
        end: ta.selectionEnd ?? v.length
      };
    }
    if (!isControlled) setInternal(v);
    onChange?.(v);
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = `${Math.min(ta.scrollHeight, 192)}px`;
      if (restore && document.activeElement === ta) {
        const max = v.length;
        ta.setSelectionRange(
          Math.min(restore.start, max),
          Math.min(restore.end, max)
        );
      }
    }
  };
  const submit = async () => {
    if (!text.trim() || busy || disabled) return;
    await onSubmit(text);
    if (!isControlled) setInternal("");
    if (taRef.current) taRef.current.style.height = "auto";
  };
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };
  const slashState = useMemo(() => {
    const lines = text.split("\n");
    const last = lines[lines.length - 1] ?? "";
    const m = last.match(/^\/([\w-]*)$/);
    if (!m || slashSkills.length === 0) return { open: false, filter: "", lastLine: last };
    const filter = (m[1] ?? "").toLowerCase();
    return { open: true, filter, lastLine: last };
  }, [text, slashSkills]);
  const filteredSlash = useMemo(() => {
    if (!slashState.open) return [];
    const f = slashState.filter;
    return slashSkills.filter(
      (s) => f === "" || s.name.toLowerCase().startsWith(f) || s.name.toLowerCase().includes(f)
    ).slice(0, 8);
  }, [slashSkills, slashState]);
  const insertSlashSkill = (name) => {
    const lines = text.split("\n");
    lines[lines.length - 1] = `/${name} `;
    const next = lines.join("\n");
    setText(next);
    requestAnimationFrame(() => taRef.current?.focus());
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-composer", busy && "ds-composer--busy"), children: [
    onSurfaceModeChange && /* @__PURE__ */ jsx(
      "div",
      {
        className: "ds-composer__modes",
        role: "radiogroup",
        "aria-label": "\u0420\u0435\u0436\u0438\u043C \u0440\u0430\u0431\u043E\u0447\u0435\u0439 \u043E\u0431\u043B\u0430\u0441\u0442\u0438",
        children: ["chat", "cowork", "code"].map((m) => /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": m === surfaceMode,
            className: cn(
              "ds-composer__mode-pill",
              m === surfaceMode && "ds-composer__mode-pill--active"
            ),
            onClick: () => onSurfaceModeChange(m),
            children: SURFACE_LABELS[m]
          },
          m
        ))
      }
    ),
    onModeChange && /* @__PURE__ */ jsx("div", { className: "ds-composer__modes", role: "radiogroup", "aria-label": "\u0420\u0435\u0436\u0438\u043C \u0430\u0433\u0435\u043D\u0442\u0430", children: modes.map((m) => /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        role: "radio",
        "aria-checked": m === mode,
        className: cn(
          "ds-composer__mode-pill",
          m === mode && "ds-composer__mode-pill--active"
        ),
        onClick: () => onModeChange(m),
        children: MODE_LABELS[m]
      },
      m
    )) }),
    /* @__PURE__ */ jsxs("div", { className: "ds-composer__row", style: { position: "relative" }, children: [
      onAttach && /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u041F\u0440\u0438\u043A\u0440\u0435\u043F\u0438\u0442\u044C \u0444\u0430\u0439\u043B",
          icon: /* @__PURE__ */ jsx(Paperclip, {}),
          onClick: onAttach,
          size: "md",
          variant: "ghost"
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "ds-composer__field", children: [
        slashState.open && filteredSlash.length > 0 && /* @__PURE__ */ jsx(
          "div",
          {
            className: "ds-composer__slash-menu",
            role: "listbox",
            "aria-label": "Skills",
            children: filteredSlash.map((s) => /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                role: "option",
                className: "ds-composer__slash-item",
                onMouseDown: (ev) => {
                  ev.preventDefault();
                  insertSlashSkill(s.name);
                },
                children: [
                  /* @__PURE__ */ jsxs("span", { className: "ds-composer__slash-name", children: [
                    "/",
                    s.name
                  ] }),
                  s.description ? /* @__PURE__ */ jsx("span", { className: "ds-composer__slash-desc", children: s.description }) : null
                ]
              },
              s.name
            ))
          }
        ),
        /* @__PURE__ */ jsx(
          "textarea",
          {
            ref: taRef,
            value: text,
            onChange: (e) => setText(e.target.value),
            onKeyDown: handleKey,
            placeholder,
            disabled,
            rows: 1,
            className: "ds-composer__textarea",
            "aria-label": "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435"
          }
        )
      ] }),
      onVoice && /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u0413\u043E\u043B\u043E\u0441\u043E\u0432\u043E\u0439 \u0432\u0432\u043E\u0434",
          icon: /* @__PURE__ */ jsx(Mic, {}),
          onClick: onVoice,
          size: "md",
          variant: "ghost"
        }
      ),
      busy && onStop ? /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u041E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C",
          icon: /* @__PURE__ */ jsx(Square, { size: 14, fill: "currentColor" }),
          onClick: () => void Promise.resolve(onStop()),
          size: "md",
          variant: "primary",
          className: "ds-composer__stop-btn"
        }
      ) : /* @__PURE__ */ jsx(
        IconButton,
        {
          "aria-label": "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C",
          icon: /* @__PURE__ */ jsx(Send, {}),
          onClick: submit,
          disabled: !text.trim() || disabled || busy,
          size: "md",
          variant: "primary"
        }
      )
    ] })
  ] });
}
function ChatTabs({ tabs, activeId, onSelect, onClose, onNew }) {
  return /* @__PURE__ */ jsxs("div", { role: "tablist", "aria-label": "\u0427\u0430\u0442\u044B", className: "ds-chat-tabs", children: [
    tabs.map((tab) => /* @__PURE__ */ jsxs(
      "div",
      {
        role: "tab",
        "aria-selected": tab.id === activeId,
        "aria-controls": `panel-${tab.id}`,
        id: `tab-${tab.id}`,
        className: cn("ds-chat-tabs__tab", tab.id === activeId && "ds-chat-tabs__tab--active"),
        children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: "ds-chat-tabs__title",
              onClick: () => onSelect(tab.id),
              tabIndex: tab.id === activeId ? 0 : -1,
              children: [
                tab.title,
                tab.unread != null && tab.unread > 0 && /* @__PURE__ */ jsx("span", { className: "ds-chat-tabs__unread", children: tab.unread })
              ]
            }
          ),
          onClose && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "aria-label": `\u0417\u0430\u043A\u0440\u044B\u0442\u044C ${tab.title}`,
              onClick: () => onClose(tab.id),
              className: "ds-chat-tabs__close",
              children: /* @__PURE__ */ jsx(X, { size: 12 })
            }
          )
        ]
      },
      tab.id
    )),
    onNew && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "aria-label": "\u041D\u043E\u0432\u044B\u0439 \u0447\u0430\u0442",
        onClick: onNew,
        className: "ds-chat-tabs__new",
        children: /* @__PURE__ */ jsx(Plus, { size: 14 })
      }
    )
  ] });
}
function AgentProgressCard({
  status,
  currentStep,
  progress,
  counter,
  detail
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("ds-agent-progress", `ds-agent-progress--${status}`), children: [
    /* @__PURE__ */ jsxs("div", { className: "ds-agent-progress__top", children: [
      /* @__PURE__ */ jsxs("span", { className: "ds-agent-progress__indicator", "aria-hidden": "true", children: [
        status === "running" && /* @__PURE__ */ jsx(Loader2, { className: "ds-agent-progress__spin", size: 16 }),
        status === "success" && /* @__PURE__ */ jsx(CheckCircle2, { size: 16 }),
        status === "failed" && /* @__PURE__ */ jsx(AlertCircle, { size: 16 })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "ds-agent-progress__main", children: [
        currentStep && /* @__PURE__ */ jsx("p", { className: "ds-agent-progress__step", children: currentStep }),
        counter && /* @__PURE__ */ jsx("p", { className: "ds-agent-progress__counter", children: counter })
      ] })
    ] }),
    progress != null && /* @__PURE__ */ jsx("div", { className: "ds-agent-progress__bar", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
      "span",
      {
        className: "ds-agent-progress__bar-fill",
        style: { width: `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%` }
      }
    ) }),
    detail && /* @__PURE__ */ jsx("div", { className: "ds-agent-progress__detail", children: detail })
  ] });
}
function formatDuration(ms) {
  if (ms < 1e3) return `${ms}ms`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(1)}s`;
  const minutes = Math.floor(ms / 6e4);
  const seconds = Math.round(ms % 6e4 / 1e3);
  return `${minutes}m ${seconds}s`;
}
function ThinkingBlock({
  children,
  isActive = false,
  tokenCount,
  durationMs,
  defaultOpen = false,
  preview,
  labelActive = "\u0420\u0430\u0437\u043C\u044B\u0448\u043B\u044F\u0435\u0442\u2026",
  labelDone = "\u0420\u0430\u0437\u043C\u044B\u0448\u043B\u0435\u043D\u0438\u0435",
  className
}) {
  const [open, setOpen] = useState(defaultOpen);
  const previewText = !open && !isActive ? (preview ?? "").trim() : "";
  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((o) => !o);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "ds-thinking",
        isActive && "ds-thinking--active",
        open && "ds-thinking--open",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "ds-thinking__header",
            onClick: () => setOpen((o) => !o),
            onKeyDown: onKey,
            role: "button",
            tabIndex: 0,
            "aria-expanded": open,
            children: [
              /* @__PURE__ */ jsx("span", { className: cn("ds-thinking__icon", isActive && "ds-thinking__icon--active"), children: isActive ? "\u25D0" : "\u2234" }),
              /* @__PURE__ */ jsx("span", { className: "ds-thinking__label", children: isActive ? labelActive : labelDone }),
              !isActive && durationMs != null && durationMs > 0 && /* @__PURE__ */ jsx("span", { className: "ds-thinking__duration", title: "\u0412\u0440\u0435\u043C\u044F \u0440\u0430\u0437\u043C\u044B\u0448\u043B\u0435\u043D\u0438\u044F", children: formatDuration(durationMs) }),
              !isActive && tokenCount != null && tokenCount > 0 && /* @__PURE__ */ jsxs("span", { className: "ds-thinking__tokens", children: [
                "~",
                tokenCount,
                "t"
              ] }),
              previewText && /* @__PURE__ */ jsxs("span", { className: "ds-thinking__preview", "aria-hidden": "true", children: [
                "\u2014 ",
                previewText
              ] }),
              /* @__PURE__ */ jsx("span", { className: "ds-thinking__chevron", "aria-hidden": "true", children: "\u25B8" })
            ]
          }
        ),
        open && children && /* @__PURE__ */ jsx("div", { className: "ds-thinking__body", children })
      ]
    }
  );
}
function StatusBadge({
  kind,
  label,
  ariaLabel,
  size = "md",
  variant = "pill",
  title,
  className
}) {
  const a11y = ariaLabel ?? label ?? kind;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "ds-status-badge",
        `ds-status-badge--${kind}`,
        `ds-status-badge--${size}`,
        `ds-status-badge--${variant}`,
        className
      ),
      role: "status",
      "aria-label": a11y,
      title: title ?? a11y,
      children: [
        /* @__PURE__ */ jsx(StatusIcon, { kind }),
        label && variant !== "dot" && /* @__PURE__ */ jsx("span", { className: "ds-status-badge__label", children: label })
      ]
    }
  );
}
function StatusIcon({ kind }) {
  const base = {
    width: 12,
    height: 12,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false
  };
  switch (kind) {
    case "success":
      return /* @__PURE__ */ jsx("svg", { ...base, className: "ds-status-badge__icon", children: /* @__PURE__ */ jsx("polyline", { points: "3 8 7 12 13 4" }) });
    case "warning":
      return /* @__PURE__ */ jsxs("svg", { ...base, className: "ds-status-badge__icon", children: [
        /* @__PURE__ */ jsx("path", { d: "M8 2 L14 13 L2 13 Z" }),
        /* @__PURE__ */ jsx("line", { x1: "8", y1: "6", x2: "8", y2: "9" }),
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "11.5", r: "0.5", fill: "currentColor" })
      ] });
    case "danger":
      return /* @__PURE__ */ jsxs("svg", { ...base, className: "ds-status-badge__icon", children: [
        /* @__PURE__ */ jsx("line", { x1: "4", y1: "4", x2: "12", y2: "12" }),
        /* @__PURE__ */ jsx("line", { x1: "12", y1: "4", x2: "4", y2: "12" })
      ] });
    case "info":
      return /* @__PURE__ */ jsxs("svg", { ...base, className: "ds-status-badge__icon", children: [
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6" }),
        /* @__PURE__ */ jsx("line", { x1: "8", y1: "7", x2: "8", y2: "11" }),
        /* @__PURE__ */ jsx("circle", { cx: "8", cy: "5", r: "0.5", fill: "currentColor" })
      ] });
    case "running":
      return /* @__PURE__ */ jsx("svg", { ...base, className: "ds-status-badge__icon ds-status-badge__icon--spin", children: /* @__PURE__ */ jsx("path", { d: "M8 2 A6 6 0 1 1 2 8" }) });
    case "idle":
      return /* @__PURE__ */ jsx("svg", { ...base, className: "ds-status-badge__icon", children: /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "3", fill: "currentColor", stroke: "none" }) });
  }
}
function formatElapsed(ms) {
  const seconds = Math.floor(ms / 1e3);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}m ${remaining}s`;
}
function SpinnerWithModes({
  mode,
  theme = "default",
  elapsedMs,
  label,
  size = "md",
  className
}) {
  const visual = useMemo(() => {
    switch (mode) {
      case "idle":
        return null;
      case "thinking":
        return /* @__PURE__ */ jsxs("div", { className: "ds-spinner__thinking", children: [
          /* @__PURE__ */ jsx("div", { className: "ds-spinner__thinking-ring" }),
          /* @__PURE__ */ jsx("div", { className: "ds-spinner__thinking-ring" }),
          /* @__PURE__ */ jsx("div", { className: "ds-spinner__thinking-ring" })
        ] });
      case "tool":
        return /* @__PURE__ */ jsxs("svg", { className: "ds-spinner__tool", viewBox: "0 0 20 20", fill: "none", children: [
          /* @__PURE__ */ jsx("title", { children: "tool running" }),
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M10 2a1 1 0 011 1v1.07A6.003 6.003 0 0114.93 8H16a1 1 0 110 2h-1.07A6.003 6.003 0 0111 13.93V15a1 1 0 11-2 0v-1.07A6.003 6.003 0 015.07 10H4a1 1 0 110-2h1.07A6.003 6.003 0 019 4.07V3a1 1 0 011-1zm0 4a4 4 0 100 8 4 4 0 000-8z",
              fill: "currentColor"
            }
          )
        ] });
      case "busy":
        return /* @__PURE__ */ jsxs("div", { className: "ds-spinner__busy", children: [
          /* @__PURE__ */ jsx("span", { className: "ds-spinner__busy-dot" }),
          /* @__PURE__ */ jsx("span", { className: "ds-spinner__busy-dot" }),
          /* @__PURE__ */ jsx("span", { className: "ds-spinner__busy-dot" })
        ] });
      case "waiting":
        return /* @__PURE__ */ jsx("div", { className: "ds-spinner__waiting" });
      case "compiling":
        return /* @__PURE__ */ jsxs("svg", { className: "ds-spinner__compiling", viewBox: "0 0 20 20", children: [
          /* @__PURE__ */ jsx("title", { children: "compiling" }),
          /* @__PURE__ */ jsx("circle", { className: "ds-spinner__compiling-circle", cx: "10", cy: "10", r: "7" })
        ] });
    }
  }, [mode]);
  if (mode === "idle") return null;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "ds-spinner",
        size !== "md" && `ds-spinner--${size}`,
        theme !== "default" && `ds-spinner--${theme}`,
        className
      ),
      role: "status",
      "aria-label": label || `${mode}\u2026`,
      children: [
        /* @__PURE__ */ jsx("span", { className: "ds-spinner__visual", children: visual }),
        label && /* @__PURE__ */ jsx("span", { className: "ds-spinner__label", children: label }),
        elapsedMs != null && elapsedMs > 0 && /* @__PURE__ */ jsx("span", { className: "ds-spinner__elapsed", children: formatElapsed(elapsedMs) })
      ]
    }
  );
}
var TOOL_ICONS = {
  read_file: FileText,
  read: FileText,
  write: Edit3,
  edit: Edit3,
  patch: Edit3,
  apply_patch: Edit3,
  bash: TerminalSquare,
  shell: TerminalSquare,
  glob: Search,
  grep: Search,
  search: Search,
  fetch: Globe,
  web_search: Globe,
  code: Code2
};
var DIFF_TOOLS = /* @__PURE__ */ new Set(["edit", "write", "patch", "apply_patch"]);
function toolKey(name) {
  return name.toLowerCase().replace(/[^a-z_]/g, "");
}
function getToolIcon(name) {
  return TOOL_ICONS[toolKey(name)] ?? Hammer;
}
function formatDuration2(ms) {
  if (ms < 1e3) return `${ms}ms`;
  return `${(ms / 1e3).toFixed(1)}s`;
}
var DIFF_PATTERN_HEAD = /^[+-]{3} /m;
var DIFF_PATTERN_HUNK = /^@@/m;
function detectDiff(text) {
  return DIFF_PATTERN_HEAD.test(text) && DIFF_PATTERN_HUNK.test(text);
}
function DiffOutput({ text }) {
  const lines = useMemo(() => text.split("\n"), [text]);
  return /* @__PURE__ */ jsx("div", { className: "ds-tool__diff", children: lines.map((line, i) => {
    const prefix = line[0];
    const isAdd = prefix === "+" && !line.startsWith("+++");
    const isDel = prefix === "-" && !line.startsWith("---");
    const isHunk = line.startsWith("@@");
    const cls = isAdd ? "ds-tool__diff-line--add" : isDel ? "ds-tool__diff-line--del" : isHunk ? "ds-tool__diff-line--hunk" : "ds-tool__diff-line--ctx";
    return /* @__PURE__ */ jsxs("div", { className: cn("ds-tool__diff-line", cls), children: [
      /* @__PURE__ */ jsx("span", { className: "ds-tool__diff-gutter", children: prefix ?? " " }),
      /* @__PURE__ */ jsx("span", { className: "ds-tool__diff-text", children: isAdd || isDel ? line.slice(1) : line })
    ] }, `${i}-${line.slice(0, 8)}`);
  }) });
}
function TruncatedOutput({
  text,
  diff,
  truncateLines,
  outputNode
}) {
  const [expanded, setExpanded] = useState(false);
  const lines = useMemo(() => text.split("\n"), [text]);
  const overflows = lines.length > truncateLines;
  const showAll = !overflows || expanded;
  const visible = showAll ? text : lines.slice(0, truncateLines).join("\n");
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    diff ? /* @__PURE__ */ jsx(DiffOutput, { text: visible }) : outputNode && showAll ? /* @__PURE__ */ jsx("div", { className: "ds-tool__output", children: outputNode }) : /* @__PURE__ */ jsx("pre", { className: "ds-tool__pre", children: visible }),
    overflows && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "ds-tool__truncate",
        onClick: () => setExpanded((v) => !v),
        children: expanded ? `\u25B2 \u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C (${lines.length} \u0441\u0442\u0440\u043E\u043A)` : `\u25BC \u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0432\u0441\u0435 ${lines.length} \u0441\u0442\u0440\u043E\u043A`
      }
    )
  ] });
}
function ToolUseCard({
  name,
  summary,
  detail,
  status,
  input,
  output,
  outputNode,
  defaultExpanded = false,
  durationMs,
  truncateLines = 40,
  isDiff,
  className
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const headerId = useId();
  const Icon = getToolIcon(name);
  const showDiff = output != null && (isDiff ?? (DIFF_TOOLS.has(toolKey(name)) && detectDiff(output)));
  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setExpanded((e) => !e);
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "ds-tool",
        `ds-tool--${status}`,
        expanded && "ds-tool--expanded",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            id: headerId,
            className: "ds-tool__header",
            onClick: () => setExpanded((e) => !e),
            onKeyDown: onKey,
            role: "button",
            tabIndex: 0,
            "aria-expanded": expanded,
            children: [
              /* @__PURE__ */ jsx("span", { className: "ds-tool__icon", "aria-hidden": "true", children: /* @__PURE__ */ jsx(Icon, { size: 14 }) }),
              /* @__PURE__ */ jsxs("span", { className: "ds-tool__title", children: [
                /* @__PURE__ */ jsx("span", { className: "ds-tool__name", children: summary ?? name }),
                summary && /* @__PURE__ */ jsx("span", { className: "ds-tool__detail", children: detail ?? name })
              ] }),
              /* @__PURE__ */ jsx(
                StatusBadge,
                {
                  kind: status === "done" ? "success" : status === "error" ? "danger" : "running",
                  ariaLabel: status === "done" ? "\u0412\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u043E" : status === "error" ? "\u041E\u0448\u0438\u0431\u043A\u0430" : "\u0412\u044B\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F",
                  size: "sm",
                  variant: "inline"
                }
              ),
              durationMs != null && /* @__PURE__ */ jsx("span", { className: "ds-tool__duration", children: formatDuration2(durationMs) }),
              showDiff && /* @__PURE__ */ jsx("span", { className: "ds-tool__diff-badge", children: "diff" }),
              /* @__PURE__ */ jsx("span", { className: "ds-tool__chevron", "aria-hidden": "true", children: "\u25B8" })
            ]
          }
        ),
        expanded && /* @__PURE__ */ jsxs(Fragment, { children: [
          input && /* @__PURE__ */ jsxs("div", { className: "ds-tool__section", children: [
            /* @__PURE__ */ jsx("div", { className: "ds-tool__section-label", children: "\u0417\u0430\u043F\u0440\u043E\u0441" }),
            /* @__PURE__ */ jsx("pre", { className: "ds-tool__pre", children: input })
          ] }),
          (output || outputNode) && /* @__PURE__ */ jsxs("div", { className: "ds-tool__section", children: [
            /* @__PURE__ */ jsx("div", { className: "ds-tool__section-label", children: status === "error" ? "\u041E\u0448\u0438\u0431\u043A\u0430" : "\u0420\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442" }),
            output != null ? /* @__PURE__ */ jsx(
              TruncatedOutput,
              {
                text: output,
                diff: Boolean(showDiff),
                truncateLines,
                outputNode
              }
            ) : /* @__PURE__ */ jsx("div", { className: "ds-tool__output", children: outputNode })
          ] })
        ] })
      ]
    }
  );
}
function defaultRender(content) {
  if (!content) return null;
  return /* @__PURE__ */ jsx("p", { className: "ds-stream__plain", children: content });
}
function StreamingMessage({
  content,
  isStreaming,
  render = defaultRender,
  className
}) {
  const [showFadingCursor, setShowFadingCursor] = useState(false);
  const wasStreamingRef = useRef(isStreaming);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && content.length > 0) {
      setShowFadingCursor(true);
      const timer = setTimeout(() => setShowFadingCursor(false), 300);
      wasStreamingRef.current = isStreaming;
      return () => clearTimeout(timer);
    }
    wasStreamingRef.current = isStreaming;
    return void 0;
  }, [isStreaming, content.length]);
  const { stableContent, tailContent } = useMemo(() => {
    if (!isStreaming) return { stableContent: content, tailContent: "" };
    const lastDoubleNewline = content.lastIndexOf("\n\n");
    if (lastDoubleNewline < 0) {
      return { stableContent: "", tailContent: content };
    }
    return {
      stableContent: content.slice(0, lastDoubleNewline),
      tailContent: content.slice(lastDoubleNewline)
    };
  }, [content, isStreaming]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn("ds-stream", isStreaming && "ds-stream--streaming", className),
      "aria-live": "polite",
      "aria-atomic": "false",
      children: [
        stableContent && /* @__PURE__ */ jsx("div", { className: "ds-stream__stable", children: render(stableContent, "stable") }),
        tailContent && /* @__PURE__ */ jsx("div", { className: "ds-stream__tail", children: render(tailContent, "tail") }),
        !isStreaming && !stableContent && !tailContent && content && /* @__PURE__ */ jsx("div", { className: "ds-stream__stable", children: render(content, "stable") }),
        isStreaming && !tailContent && /* @__PURE__ */ jsx("span", { className: "ds-stream__cursor", "aria-hidden": "true" }),
        showFadingCursor && !isStreaming && /* @__PURE__ */ jsx(
          "span",
          {
            className: "ds-stream__cursor ds-stream__cursor--fading",
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function MessageGroup({
  summary,
  badge,
  depth = 0,
  status,
  counter,
  defaultCollapsed = true,
  children,
  className
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const onKey = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen((o) => !o);
    }
  };
  const indent = Math.min(depth, 4) * 12;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "ds-msg-group",
        `ds-msg-group--depth-${Math.min(depth, 4)}`,
        status && `ds-msg-group--${status}`,
        open && "ds-msg-group--open",
        className
      ),
      style: depth > 0 ? { marginLeft: indent } : void 0,
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "ds-msg-group__header",
            onClick: () => setOpen((o) => !o),
            onKeyDown: onKey,
            role: "button",
            tabIndex: 0,
            "aria-expanded": open,
            children: [
              /* @__PURE__ */ jsx("span", { className: "ds-msg-group__chevron", "aria-hidden": "true", children: "\u25B8" }),
              /* @__PURE__ */ jsx("span", { className: "ds-msg-group__summary", children: summary }),
              badge && /* @__PURE__ */ jsx("code", { className: "ds-msg-group__badge", children: badge }),
              counter && /* @__PURE__ */ jsx("span", { className: "ds-msg-group__counter", children: counter }),
              status === "running" && /* @__PURE__ */ jsx("span", { className: "ds-msg-group__status", children: /* @__PURE__ */ jsx(SpinnerWithModes, { mode: "busy", size: "sm" }) }),
              status === "done" && /* @__PURE__ */ jsx(
                "span",
                {
                  className: "ds-msg-group__status ds-msg-group__status--done",
                  "aria-label": "\u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u043E",
                  children: "\u2713"
                }
              ),
              status === "error" && /* @__PURE__ */ jsx(
                "span",
                {
                  className: "ds-msg-group__status ds-msg-group__status--error",
                  "aria-label": "\u043E\u0448\u0438\u0431\u043A\u0430",
                  children: "\u2717"
                }
              )
            ]
          }
        ),
        open && /* @__PURE__ */ jsx("div", { className: "ds-msg-group__body", children })
      ]
    }
  );
}

export { AgentProgressCard, ChatTabs, Composer, MessageBubble, MessageGroup, SpinnerWithModes, StatusBadge, StreamingMessage, ThinkingBlock, ToolUseCard };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map